<template>
  <div class="gm-step gm-step4">
    <!-- Postal (Closem.ai) content -->
    <section v-if="localModel.sendoption === 'postal'" class="gs-wizard-card">
      <h2>{{ $t('frontend_groovemail_campaigns_create_postal_integration_postal_content_page_header') }}</h2>
      <p class="gm-step4__postal-intro">
        <span>{{
          $t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_campaign_content_sub_heading_part_a'
          )
        }}</span>
        <button
          type="button"
          class="gm-link"
          @click="redirectToCLosemSignupPage"
        >closem.ai</button>
        <span>{{
          $t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_campaign_content_sub_heading_part_b'
          )
        }}</span>
        <button
          v-if="fetchClosemIntegration()"
          type="button"
          class="gm-link"
          @click="redirectToCLosemDashboard"
        >{{
          $t('frontend_groovemail_campaigns_create_postal_integration_postal_click_here_to_create_postals')
        }}</button>
      </p>
      <div
        class="gm-step4__postal-signup"
        v-if="localModel.sendoption === 'postal' && !fetchClosemIntegration()"
      >
        <gs-button
          variant="primary"
          @click="redirectToCLosemSignupPage()"
        >{{ $t('frontend_groovemail_campaigns_create_postal_integration_postal_signup_closem') }}</gs-button>
      </div>
      <div
        v-if="localModel.sendoption === 'postal' && fetchClosemIntegration()"
        class="gm-fields"
      >
        <gs-field
          :label="$t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_postal_content_label')"
          :error="
            isPostalContentSubmitted && !localModel.postal_sequence_id
              ? $t('frontend_groovemail_campaigns_create_select_campaign_to_proceed_for_postal')
              : ''
          "
        >
          <treeselect
            class="custom-treeselect"
            :class="{
              'error text-danger': isPostalContentSubmitted && !localModel.postal_sequence_id
            }"
            v-model="localModel.postal_sequence_id"
            :multiple="false"
            :options="postalCampaignsList"
            :closeOnSelect="closeOnSelect"
            :clearable="clearable"
            :clearOnSelect="clearOnSelect"
            :placeholder="
              $t('frontend_select')
            "
          />
        </gs-field>
        <div class="gm-step4__credits">
          <div class="gm-step4__credits-head">
            <h3>{{ $t('frontend_groovemail_campaigns_create_postal_integration_postal_credits_label') }}</h3>
            <gs-button
              variant="outline"
              size="sm"
              @click="redirectToClosemCreditsPage()"
            >{{ $t('frontend_groovemail_campaigns_create_postal_integration_postal_purchase_credits_link') }}</gs-button>
          </div>
          <p class="gm-step4__credits-copy">
            {{ $t('frontend_groovemail_campains_step3_campaign_would_be_sent') }}
            <strong>{{ localModel.filledemails }} {{ $t('frontend_groovemail_campains_step3_people') }}.</strong>
          </p>
          <p class="gm-step4__credits-copy">
            {{ $t('frontend_groovemail_campaigns_create_postal_type_address_note') }}
          </p>
          <div class="gm-stat-grid">
            <div class="gm-stat">
              <span class="gm-stat__label">{{ $t('frontend_groovemail_campaigns_create_postal_integration_postal_credits_large_us') }}</span>
              <strong class="gm-stat__value">{{ credits_large_us }}</strong>
            </div>
            <div class="gm-stat">
              <span class="gm-stat__label">{{ $t('frontend_groovemail_campaigns_create_postal_integration_postal_credits_small_us') }}</span>
              <strong class="gm-stat__value">{{ credits_small_us }}</strong>
            </div>
            <div class="gm-stat">
              <span class="gm-stat__label">{{ $t('frontend_groovemail_campaigns_create_postal_integration_postal_credits_large_int') }}</span>
              <strong class="gm-stat__value">{{ credits_large_int }}</strong>
            </div>
            <div class="gm-stat">
              <span class="gm-stat__label">{{ $t('frontend_groovemail_campaigns_create_postal_integration_postal_credits_small_int') }}</span>
              <strong class="gm-stat__value">{{ credits_small_int }}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Sender -->
    <section
      v-if="localModel.sendoption !== 'postal'"
      class="gs-wizard-card"
    >
      <h2>{{ $t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_campaign_send_campaign_as_text') }}</h2>
      <p>Choose the sender this broadcast goes out from.</p>
      <div class="gm-step4__sender">
        <gs-field
          class="gm-step4__sender-field"
          :error="
            (isEmailContentSubmitted || isSmsContentSubmitted) &&
            ((localModel.sender_id && localModel.sender_id === -1) ||
              !localModel.sender_id)
              ? $t('frontend_groovemail_campaigns_create_select_sender_to_proceed')
              : ''
          "
        >
          <gd-dropdown
            class="gm-dropdown"
            size="full"
            variant="base"
            :placeholder="$t('frontend_groovemail_sequences_email_select_sender')"
            v-model="localModel.sender_id"
            :disabled="senderList.length === 0"
            :options="senderList"
            @input="updatedSender"
          />
        </gs-field>
        <gs-button
          variant="outline"
          @click="localModel.sendoption === 'sms' ? openCreateSmsSenderModal() : openCreateEmailSenderModal()"
        >
          <gs-icon name="plus" size="16"></gs-icon>
          {{ $t('frontend_groovemail_settings_integeration_add_new_integeration') }}
        </gs-button>
      </div>
    </section>

    <!-- SMS message -->
    <section v-if="localModel.sendoption === 'sms'" class="gs-wizard-card">
      <h2>{{ $t('frontend_groovemail_campain_message') }}</h2>
      <p>Write the text message. Insert a variable to personalise it for each contact.</p>
      <div class="gm-step4__toolbar">
        <gs-button
          variant="ghost"
          size="sm"
          @click="openPreview()"
        >
          <PreviewIcon class="gm-step4__toolbar-icon"/>
          {{ $t('frontend_preview') }}
        </gs-button>
        <div
          v-click-outside="closeSmsVariables"
          class="gm-variables"
          @click="openSmsVariables"
        >
          <gs-button variant="outline" size="sm">
            <gs-icon name="plus" size="16"></gs-icon>
            {{ $t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_insert_varaibles') }}
          </gs-button>
          <div
            class="gm-variables__menu"
            v-if="showSmsVariableDropdown"
          >
            <text-input
              class="gm-input"
              variant="base"
              v-model="smsVariableSearch"
              @input="changedSearchVariable"
              :placeholder="$t('frontend_groovemail_tables_search_query')"
            >
              <SearchIcon slot="prependIcon"/>
            </text-input>
            <VuePerfectScrollbar class="gm-variables__list">
              <button
                v-for="(
                  option, index
                ) in dynamic_variables_sms_content_subject"
                :key="index"
                type="button"
                class="gm-variables__item"
                @click="copySmsvariable(option)"
              >
                {{ option.label }}
              </button>
            </VuePerfectScrollbar>
          </div>
        </div>
      </div>
      <gs-field
        :error="isSmsContentSubmitted && !localModel.smscontent
          ? $t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_sms_content_empty')
          : ''"
      >
        <text-area
          class="gm-input"
          variant="base"
          id="sms-text-area"
          v-model="localModel.smscontent"
          style="height: 260px"
          :placeholder="$t('frontend_groovemail_campaigns_create_broadcast_enter_sms_content')"
          :validator="$v.localModel.smscontent"
        />
      </gs-field>
    </section>

    <!-- Email: subject & preview text -->
    <section v-if="localModel.sendoption === 'email'" class="gs-wizard-card">
      <h2>Subject line</h2>
      <p>What recipients see in their inbox before they open the email.</p>
      <div class="gm-fields gm-step4__fields">
        <div>
          <div class="gm-field-label">
            <span>{{ $t('frontend_groovemail_campains_step4_subject') }}</span>
            <button
              type="button"
              aria-label="About dynamic variables"
              v-tooltip="{
                content: $t('frontend_add_dynamic_variables_to_subject'),
                placement: 'right-start',
                classes: ['info'],
                targetClasses: ['it-has-a-tooltip'],
                offset: 10,
                delay: {
                  show: 200,
                  hide: 300,
                },
              }"
            >
              <InfoIcon/>
            </button>
          </div>
          <div class="gm-variables gm-variables--inline">
            <text-input
              class="gm-input gm-step4__subject-input"
              variant="base"
              v-model="localModel.emailsubject"
              :placeholder="$t('frontend_groovemail_campaigns_enter_subject')"
              :validator="$v.localModel.emailsubject"
            />
            <div
              v-click-outside="closeSubjectVariables"
              class="gm-variables__anchor"
              @click="openSubjectVariables"
            >
              <button
                type="button"
                class="gm-variables__inline-trigger"
                aria-label="Insert variable"
                :title="$t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_insert_varaibles')"
              >
                <gs-icon name="plus" size="16"></gs-icon>
              </button>
              <div
                class="gm-variables__menu"
                v-if="showSubjectDropdown"
                @click.stop
              >
                <text-input
                  class="gm-input"
                  variant="base"
                  v-model="emailSubjectVariableSearch"
                  @input="changedSearchVariable"
                  :placeholder="$t('frontend_groovemail_tables_search_query')"
                >
                  <SearchIcon slot="prependIcon"/>
                </text-input>
                <VuePerfectScrollbar class="gm-variables__list">
                  <button
                    v-for="(
                      option, index
                    ) in dynamic_variables_sms_content_subject"
                    :key="index"
                    type="button"
                    class="gm-variables__item"
                    @click="copyVariableInSubject(option)"
                  >
                    {{ option.label }}
                  </button>
                </VuePerfectScrollbar>
              </div>
            </div>
          </div>
        </div>
        <gs-field
          :label="$t('frontend_groovemail_campains_step4_preview')"
          hint="Shown next to the subject in most inboxes."
        >
          <text-input
            class="gm-input"
            variant="base"
            v-model="localModel.emailpreviewtext"
            :placeholder="$t('frontend_groovemail_campaigns_enter_preview')"
          />
        </gs-field>
      </div>
    </section>

    <!-- Email: editor type -->
    <section v-if="localModel.sendoption === 'email'" class="gs-wizard-card">
      <h2>{{ $t('frontend_groovemail_email_types_label') }}</h2>
      <p>Design it in the professional email editor, or compose with the standard rich text editor and its HTML source tab.</p>
      <div class="gm-choice-grid">
        <div
          v-for="(option, index) in email_type_options"
          :key="index"
          class="gm-choice"
        >
          <gd-custom-radio
            v-model="localModel.editor_type"
            :option="option"
            value="localModel.editor_type"
          />
        </div>
      </div>
    </section>

    <!-- Email: message -->
    <section v-if="localModel.sendoption === 'email'" class="gs-wizard-card">
      <h2>{{ $t('frontend_groovemail_campain_message') }}</h2>
      <p v-if="localModel.editor_type === 3">Open the professional email editor to choose a design, edit its content, and preview it before you move on.</p>
      <p v-else>Write the email below. Use the toolbar to format text and add images or links.</p>
      <div v-if="localModel.editor_type === 3 && showEmailComposers" class="gm-step4__builder">
        <div class="gm-step4__template-name" v-if="localModel.template_name">
          <span>{{ $t('frontend_groovemail_email_builder_past_template_email_name') }}</span>
          <strong>{{ localModel.template_name }}</strong>
        </div>
        <div class="gm-step4__builder-actions">
          <gs-button
            variant="primary"
            @click="openEmailBuilder()"
          >{{ localModel.email_id
            ? $t('frontend_groovemail_email_builder_edit_your_email_placeholder')
            : $t('frontend_groovemail_email_builder_compose_your_email') }}</gs-button>
          <gs-button
            variant="outline"
            size="icon"
            aria-label="Preview email"
            :title="$t('frontend_preview')"
            :disabled="!localModel.emailcontent"
            @click="localModel.emailcontent ? openPreview() : null"
          >
            <PreviewIcon class="gm-step4__preview-icon"/>
          </gs-button>
        </div>
      </div>
      <div v-if="localModel.editor_type !== 3 && showEmailComposers" class="gm-step4__editor">
        <email-basic-editor
          ref="campaign-email-editor"
          :editorOptions="campaignEditorOptions"
          :editorContent="localModel.emailcontent"
          :editorNodeWithNoMargin="true"
          :editorWithBlockedLinkRestriction="true"
          :editorDynamicVariables="builderVariables"
          openedFrom="campaign"
          :allowEditorPreview="true"
          :allowEditorHtmlView="false"
          :currentEditorView="localModel.editor_type"
          @editorContentUpdated="localModel.emailcontent = $event"
          @templateSelected="localModel.emailsubject = $event.subject; localModel.emailpreviewtext = $event.previewText"
        />
      </div>
      <p
        class="gm-field-error"
        v-if="checkValidEmailContent()"
      >
        {{
          $t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_email_content_empty'
          )
        }}
      </p>
    </section>

    <!-- Email: send a test -->
    <section v-if="localModel.sendoption === 'email'" class="gs-wizard-card gm-step4__test">
      <h2>{{ $t('frontend_groovemail_send_test_email_label') }}</h2>
      <p>Send yourself a copy to check the subject, links and images before it goes out.</p>
      <SendTestEmail
        ref="sendTestMail"
        @showLoader="showLoader"
        @hideLoader="hideLoader"
        @proceeedSendingTestEmail="proceeedSendingTestEmail"
      />
    </section>

    <gs-named-modal
      id="create-email-sender-wrapper"
      name="create-email-sender-modal"
      width="720px"
      max-width="100%"
      :title="$t('frontend_groovemail_settings_sender_settings')"
    >
      <create-email-sender
        ref="createEmailSender"
        @emailSenderCreated="emailSenderCreated"
      >
      </create-email-sender>
      <div class="gm-modal-actions gm-modal-actions--padded">
        <gs-button
          variant="outline"
          @click="closeCreateEmailSenderModal()"
        >{{ $t('frontend_cancel') }}</gs-button>
        <gs-button
          variant="primary"
          @click="createNewEmailSender()"
        >{{ $t('frontend_groovemail_create_sender_route_page_title') }}</gs-button>
      </div>
    </gs-named-modal>

    <gs-named-modal
      id="create-sms-sender-wrapper"
      name="create-sms-sender-modal"
      width="720px"
      max-width="100%"
      :title="$t('frontend_groovemail_settings_integeration_settings')"
    >
      <create-sms-sender
        ref="createSmsSender"
        @smsSenderCreated="smsSenderCreated"
      >
      </create-sms-sender>
      <div class="gm-modal-actions gm-modal-actions--padded">
        <gs-button
          variant="outline"
          @click="closeCreateSmsSenderModal()"
        >{{ $t('frontend_cancel') }}</gs-button>
        <gs-button
          variant="primary"
          @click="createNewSmsSender()"
        >{{ $t('frontend_groovemail_create_sender_route_page_title') }}</gs-button>
      </div>
    </gs-named-modal>

    <gs-named-modal
      id="show-emailpreview-modal"
      width="80%"
      max-width="100%"
      height="90vh"
      name="campaign-email-preview-modal"
      :title="$t('frontend_preview')"
    >
      <div class="gm-modal-body gm-step4__email-preview">
        <div
          class="gm-step4__email-preview-frame"
          v-html="
            localModel.emailcontent +
            `<p style='margin: 0px'><br></p>` +
            unsubscribeContent
          "
        ></div>
      </div>
    </gs-named-modal>

    <gs-named-modal
      id="campaign-message-wrapper"
      name="campaign-sms-preview-modal"
      width="600px"
      max-width="100%"
      :title="`${$t('frontend_preview')} ${$t(
        'frontend_groovemail_campains_show_message'
      )}`"
    >
      <campaign-preview
        class="gm-modal-body"
        :campaignMethod="localModel.sendoption"
        :campaignMessage="localModel.smscontent"
      />
    </gs-named-modal>

    <gd-modal
      name="campaign-email-templates-modal"
      class="gm-professional-email-templates-modal"
      width="95vw"
      height="95vh"
      :title="$t('frontend_groovemail_modal_email_template_header')"
    >
      <div class="w-full py-4 gm-professional-email-templates-modal__body">
        <email-gallery @choose="selectProfessionalTemplate" />
      </div>
    </gd-modal>

    <gd-modal
      name="campaign-professional-email-editor-modal"
      class="gm-professional-email-editor-modal"
      width="95vw"
      height="95vh"
      :title="professionalTemplate ? professionalTemplate.name : 'Professional email editor'"
    >
      <div class="w-full py-4 gm-professional-email-editor-modal__body">
        <email-document-editor
          v-if="professionalTemplate"
          :key="professionalTemplate.id"
          :template="professionalTemplate"
          @change="professionalTemplate = Object.assign({}, professionalTemplate, $event)"
        />
        <div class="gm-actions gm-professional-email-editor-actions">
          <gs-button variant="outline" @click="closeProfessionalEditor">Cancel</gs-button>
          <gs-button :disabled="professionalEditorSaving" @click="saveProfessionalTemplate">
            {{ professionalEditorSaving ? 'Saving…' : 'Use this email' }}
          </gs-button>
        </div>
      </div>
    </gd-modal>

    <!-- Confirmation editor type change modal -->
    <confirm-modal
      :ref="`step3-confirmation-editor-type`"
      :name="`step3-confirmation-editor-type`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :hideClose="true"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_email_type_change_warning')"
      @confirm="confirmEditorTypeChange"
      @close="closeConfirmEditorTypeChange"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import ClickOutside from 'vue-click-outside';
import { required } from 'vuelidate/lib/validators';
import Treeselect from '@riophae/vue-treeselect';
import auth from '@groovepages/gd-lib/auth';
import EmailBasicEditor from '@/components/EmailBasicEditor.vue';
import { syncEditorContent } from '@/components/emailContentValidation';

import themeConfig from '@/theme/themeConfig.js';

import CampaignPreview from './campaign-preview.vue';
import CreateSmsSender from '../settings/create_integeration.vue';
import CreateEmailSender from '../settings/create.vue';
import SendTestEmail from '@/components/send-test-email';
import EmailGallery from '@/features/studio/EmailGallery.vue';
import EmailDocumentEditor from '@/features/studio/EmailDocumentEditor.vue';
import { clone } from '@/features/studio/contracts';

import '@riophae/vue-treeselect/dist/vue-treeselect.css';

// import icons
import InfoIcon from '@/assets/images/icons/new/info_new.svg';
import PreviewIcon from '@/assets/images/icons/new/preview_new.svg';
import PlusIcon from '@/assets/images/icons/new/plus-small_new.svg';
import SearchIcon from '@/assets/images/icons/new/search_new.svg';

export default {
  name: 'step4',
  props: {
    model: {
      required: true,
    },
    integrations: {
      required: true,
    },
  },

  directives: {
    ClickOutside,
  },

  data() {
    return {
      credits_large_us: 0,
      credits_small_us: 0,
      credits_large_int: 0,
      credits_small_int: 0,
      changesMadeInStep: false,
      loaderColor: themeConfig.appPrimaryColor,
      isLoading: false,
      fullPageLoader: true,
      isBroadcastsPageHTMLView: false,
      imagePickerVal: '',
      localModel: this.model,
      editorTypeChangedInternally: false,
      showEmailComposers: true,
      dynamic_variables_sms_subject: [
        {
          label: this.$t('frontend_groovemail_contacts_step2_first_name'),
          value: '{first_name}',
        },
        {
          label: this.$t('frontend_groovemail_contacts_step2_last_name'),
          value: '{last_name}',
        },
        {
          label: this.$t('frontend_groovemail_email_show_email'),
          value: '{email}',
        },
        {
          label: this.$t('frontend_groovemail_email_show_phone'),
          value: '{phone_number}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_company'),
          value: '{company}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_adress1'),
          value: '{address_1}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_adress2'),
          value: '{address_2}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_city'),
          value: '{city}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_state'),
          value: '{state}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_country'),
          value: '{country}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_zipcode'),
          value: '{zip}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_url'),
          value: '{url}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_taxnumber'),
          value: '{tax_number}',
        },
        {
          label: this.$t('frontend_groovemail_mail_lead_index_billing'),
          value: '{billing_address}',
        },
        {
          label: this.$t('frontend_groovemail_contacts_step2_ip_address'),
          value: '{ip_address}',
        },
      ],
      builderVariables: [
        {
          name: 'Affiliate link',
          label: 'Affiliate link',
          value:  '{affilate_link}',
         },
         {
          name: this.$t('frontend_groovemail_contacts_step2_first_name'),
          label: this.$t('frontend_groovemail_contacts_step2_first_name'),
          value: '{first_name}',
        },
        {
          name: this.$t('frontend_groovemail_contacts_step2_last_name'),
          label: this.$t('frontend_groovemail_contacts_step2_last_name'),
          value: '{last_name}',
        },
        {
          name: this.$t('frontend_groovemail_email_show_email'),
          label: this.$t('frontend_groovemail_email_show_email'),
          value: '{email}',
        },
        {
          name: this.$t('frontend_groovemail_email_show_phone'),
          label: this.$t('frontend_groovemail_email_show_phone'),
          value: '{phone_number}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_company'),
          label: this.$t('frontend_groovemail_mail_lead_index_company'),
          value: '{company}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_adress1'),
          label: this.$t('frontend_groovemail_mail_lead_index_adress1'),
          value: '{address_1}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_adress2'),
          label: this.$t('frontend_groovemail_mail_lead_index_adress2'),
          value: '{address_2}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_city'),
          label: this.$t('frontend_groovemail_mail_lead_index_city'),
          value: '{city}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_state'),
          label: this.$t('frontend_groovemail_mail_lead_index_state'),
          value: '{state}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_country'),
          label: this.$t('frontend_groovemail_mail_lead_index_country'),
          value: '{country}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_zipcode'),
          label: this.$t('frontend_groovemail_mail_lead_index_zipcode'),
          value: '{zip}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_url'),
          label: this.$t('frontend_groovemail_mail_lead_index_url'),
          value: '{url}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_taxnumber'),
          label: this.$t('frontend_groovemail_mail_lead_index_taxnumber'),
          value: '{tax_number}',
        },
        {
          name: this.$t('frontend_groovemail_mail_lead_index_billing'),
          label: this.$t('frontend_groovemail_mail_lead_index_billing'),
          value: '{billing_address}',
        },
        {
          name: this.$t('frontend_groovemail_contacts_step2_ip_address'),
          label: this.$t('frontend_groovemail_contacts_step2_ip_address'),
          value: '{ip_address}',
        },
      ],
      campaignEditorOptions: {
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'], // toggled buttons
              [{ list: 'ordered' }, { list: 'bullet' }],

              ['link', 'image'],
              [{ size: ['10px', false, '18px', '32px'] }], // custom dropdown
              [{ header: [1, 2, 3, 4, 5, 6, false] }],

              [{ color: [] }, { background: [] }], // dropdown with defaults from theme
              [{ align: [] }],
            ],
            handlers: {
              image: () => this.handleImageInsertionInContent()
            }
          },
        },
      },
      sender: null,
      senders: [],
      clearable: false,
      closeOnSelect: true,
      clearOnSelect: true,
      smsVariableSearch: '',
      emailSubjectVariableSearch: '',
      isSmsContentSubmitted: false,
      isEmailContentSubmitted: false,
      isPostalContentSubmitted: false,
      showSubjectDropdown: false,
      showSmsVariableDropdown: false,
      verifiedSendersRequired: false,
      unsubscribeContent: '',
      postalCampaigns: [],
      professionalTemplate: null,
      professionalEditorOpen: false,
      professionalEditorSaving: false,

      newEmailType: null,
      oldEmailType: null,
    };
  },

  validations: {
    localModel: {
      emailsubject: {
        required,
      },
      smscontent: {
        required,
      },
      emailcontent: {
        required,
      },
    },
  },

  computed: {
    //  Can mail/Upgraded user/Sandbox mode check
    userCanMail() {
      return this.getCanMail();
    },
    //  Can mail/Upgraded user/Sandbox mode check

    senderList() {
      const senderList = [];
      if (this.senders && this.senders.length) {
        if (this.localModel.sendoption === 'email') {
          this.senders.forEach((element) => {
            senderList.push({ value: element.id, text: element.name });
          });
        } else if (this.localModel.sendoption === 'sms') {
          this.senders.forEach((element) => {
            senderList.push({ value: element.id, text: element.gateway_name });
          });
        }
      }
      return senderList;
    },

    email_type_options() {
      return [
        {
          id: 3,
          title: 'Professional email editor',
          description: 'Choose a professional design and edit it in the built-in visual editor.',
          placeholder: '',
        },
        {
          id: 1,
          title: 'Standard Rich Text Email Editor',
          description: 'Compose with a Word-style toolbar, images, links, HTML source, and preview.',
          placeholder: '',
        },
      ];
    },
    
    postalCampaignsList() {
      const postalCampaignsList = [];
      if (this.postalCampaigns && this.postalCampaigns.length) {
        this.postalCampaigns.forEach((ele) => {
          postalCampaignsList.push({ id: ele.id, label: ele.title });
        });
      }
      return postalCampaignsList;
    },
  },

  async created() {
    // show loader
    this.showLoader();

    try {
      // HTML content (editor_type 2) is now handled by the standard editor's
      // built-in HTML source tab. Keep existing campaigns editable while
      // removing the separate HTML editor choice from the new-campaign UI.
      if (this.localModel.editor_type === 2 || this.localModel.editor_type === '2') {
        this.localModel.editor_type = 1;
      }

      if (this.localModel.sendoption !== 'postal') {
        await this.fetchSendersData();
        await this.fetchCustomFieldVariable();
      }

      if (this.localModel.sendoption === 'email') {
        await this.fetchsenders();
        if (!this.localModel.emailcontent) {
          this.localModel.emailcontent = '';
        }
        this.hideLoader();
      } else if (this.localModel.sendoption === 'sms') {
        await this.fetchSmsSenders();
        this.hideLoader();
      } else if (this.localModel.sendoption === 'postal' && this.fetchClosemIntegration()) {
        await this.fetchPostalCampaigns();
        this.hideLoader();
      } else {
        this.hideLoader();
      }
      this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject;

      // hide loader
      this.hideLoader();
    } catch (error) {
      // show error message
      this.$emit('requestErrorAlert', error.response?.data?.error);
    }
  },

  components: {
    CreateEmailSender,
    CreateSmsSender,
    VuePerfectScrollbar,
    CampaignPreview,
    InfoIcon,
    PreviewIcon,
    PlusIcon,
    SearchIcon,
    EmailBasicEditor,
    EmailGallery,
    EmailDocumentEditor,
    SendTestEmail,
    Treeselect,
  },

  methods: {
    //  Can mail/Upgraded user/Sandbox mode check
    ...mapGetters('mailing', ['getCanMail']),
    //  Can mail/Upgraded user/Sandbox mode check
    
    checkChangesMadeOnThisStep() {
      return this.changesMadeInStep;
    },

    handleImageInsertionInContent() {
      this.$refs['campaign-email-editor'].handleImageInsertionInContent(); 
    },

    checkValidEmailContent() {
      let validEmailContent = false;
      if (this.isEmailContentSubmitted) {
        if (this.localModel.editor_type === 1) {
          if (this.localModel.emailcontent) {
            validEmailContent = true;
          }
        } else if (this.localModel.emailcontent || (this.localModel.emailContentSections)) {
          validEmailContent = true;
        }
      } else {
        validEmailContent = true;
      }
      return !validEmailContent;
    },

    // open confirmation editor type
    openEditorTypeConfirmation(newEmailType, oldEmailType) {
      // set new and old email type
      this.newEmailType = newEmailType;
      this.oldEmailType = oldEmailType;

      // open confirmation editor type
      this.$refs[`step3-confirmation-editor-type`].open();
    },

    // confirm editor type chnage
    confirmEditorTypeChange() {
      this.initializeEmailContentData();
      
      // reset the email content data and type
      this.$emit('saveOnEmailContentReset');

      // show email composers
      this.showEmailComposers = true;

      // reset new and old email type
      this.newEmailType = null;
      this.oldEmailType = null;
    },

    // close confirmation editor type
    closeConfirmEditorTypeChange() {
      // reset new and old email type
      this.localModel.editor_type = this.oldEmailType;
      this.editorTypeChangedInternally = true;

      this.showEmailComposers = true;

      // reset new and old email type
      this.newEmailType = null;
      this.oldEmailType = null;
    },

    async openPreview() {
      if (this.localModel.sendoption === 'email') {
        // show loader
        this.showLoader();
        try {
          const unsubscribeData = await axios.get('/setting/get-unsubscribe-layout');
          this.unsubscribeContent = unsubscribeData.data.footer_layout;

          // hide loader
          this.hideLoader();
        } catch (error) {
          // show error message
          this.$emit('requestErrorAlert', error.response?.data?.error);

          return;
        }
        this.$root.$emit('modal-open', 'campaign-email-preview-modal');
      } else if (this.localModel.sendoption === 'sms') {
        this.$root.$emit('modal-open', 'campaign-sms-preview-modal');
      }
    },

    async openEmailBuilder() {
      if (this.localModel.email_id || this.localModel.emailcontent) {
        let document = this.localModel.emailContentSections;
        if (typeof document === 'string') {
          try { document = JSON.parse(document); } catch (error) { document = null; }
        }
        this.professionalTemplate = {
          id: 'campaign-current-email',
          name: this.localModel.template_name || 'Current email',
          subject: this.localModel.emailsubject || '',
          previewText: this.localModel.emailpreviewtext || '',
          document: document && document.version === 1 && Array.isArray(document.blocks) ? document : null,
          bodyHtml: this.localModel.emailcontent || ''
        };
        this.professionalEditorOpen = true;
        this.$root.$emit('modal-open', 'campaign-professional-email-editor-modal');
      } else {
        this.$root.$emit('modal-open', 'campaign-email-templates-modal');
      }
    },

    selectProfessionalTemplate(template) {
      this.professionalTemplate = clone(template);
      this.professionalEditorOpen = true;
      this.$root.$emit('modal-close', 'campaign-email-templates-modal');
      this.$root.$emit('modal-open', 'campaign-professional-email-editor-modal');
    },

    closeProfessionalEditor() {
      this.professionalEditorOpen = false;
      this.professionalTemplate = null;
      this.$root.$emit('modal-close', 'campaign-professional-email-editor-modal');
    },

    saveProfessionalTemplate() {
      if (!this.professionalTemplate || !this.professionalTemplate.bodyHtml) return;
      this.professionalEditorSaving = true;
      this.localModel.emailcontent = this.professionalTemplate.bodyHtml;
      this.localModel.emailContentSections = this.professionalTemplate.document || '';
      this.localModel.email_id = null;
      this.localModel.template_name = this.professionalTemplate.name || 'Professional email';
      if (this.professionalTemplate.subject) this.localModel.emailsubject = this.professionalTemplate.subject;
      if (this.professionalTemplate.previewText) this.localModel.emailpreviewtext = this.professionalTemplate.previewText;
      this.professionalEditorSaving = false;
      this.closeProfessionalEditor();
    },

    initializeEmailContentData() {
      this.localModel.emailcontent = '';
      this.localModel.emailContentSections = '';
      this.localModel.email_id = null;
      this.localModel.template_name = '';
      this.localModel.template_folder = null;
    },

    async updatedSender(data) {
      if (data && data === 'createSenderOption1') {
        this.localModel.sender_id = null;
        if (this.localModel.sendoption === 'email') {
          this.openCreateEmailSenderModal();
        } else if (this.localModel.sendoption === 'sms') {
          this.openCreateSmsSenderModal();
        }
      }
    },

    showLoader() {
      this.$emit('showLoader');
    },

    hideLoader() {
      this.$emit('hideLoader');
    },

    async createNewEmailSender() {
      await this.$refs.createEmailSender.createNewSender();
    },

    async emailSenderCreated(senderData) {
      await this.fetchsenders();
      if (!this.verifiedSendersRequired && (senderData && senderData.id)) {
        this.localModel.sender_id = senderData.id;
      }
      this.$root.$emit('modal-close', 'create-email-sender-modal');
    },

    openCreateEmailSenderModal() {
      this.$root.$emit('modal-open', 'create-email-sender-modal');
    },

    closeCreateEmailSenderModal() {
      this.$root.$emit('modal-close', 'create-email-sender-modal');
    },

    async createNewSmsSender() {
      await this.$refs.createSmsSender.createNewSmsSender();
    },

    async smsSenderCreated(senderData) {
      // show loader
      this.showLoader();

      try {
        const fetchedSenders = await axios.get('/sms-sender');
        this.senders = fetchedSenders.data;
        if (senderData && senderData.id) {
          this.localModel.sender_id = senderData.id;
        }

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.$emit('requestErrorAlert', error.response?.data?.error);

        return;
      }

      this.$root.$emit('modal-close', 'create-sms-sender-modal');
    },

    openCreateSmsSenderModal() {
      this.$root.$emit('modal-open', 'create-sms-sender-modal');
    },

    closeCreateSmsSenderModal() {
      this.$root.$emit('modal-close', 'create-sms-sender-modal');
    },

    async fetchSendersData() {
      // show loader
      this.showLoader();

      try {
        const sendersData = await axios.get('/email-sms-sender');
        if (sendersData.data.admin_setting) {
          this.verifiedSendersRequired = true;
        } else {
          this.verifiedSendersRequired = false;
        }

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    async fetchCustomFieldVariable() {
      // show loader
      this.showLoader();

      try {
        const customFieldsData = await axios.post('/customfield-variables');
        customFieldsData.data.forEach(fieldData => {
          this.dynamic_variables_sms_subject.push(fieldData);
          const mergeTagData = fieldData;
          mergeTagData.name = mergeTagData.label;
          this.builderVariables.push(mergeTagData);
        });

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    async fetchSmsSenders() {
      // show loader
      this.showLoader();

      try {
        const integerationData = await axios.get('/sms-sender');
        if (integerationData && integerationData.data && integerationData.data.length) {
          this.senders = integerationData.data;
        }
        if (
          (this.$route.params.id === undefined && (this.localModel.sender_id === -1 || !this.localModel.sender_id)) ||
          (this.$route.params.id && (this.localModel.sender_id === -1 || !this.localModel.sender_id))
        ) {
          for (let i = 0; i < this.senders.length; i += 1) {
            if (this.senders[i].is_default === 1) {
              this.localModel.sender_id = this.senders[i].id;
            }
          }
        }
        if (this.localModel.sender_id) {
          const sender = this.senders.find((ele) => {
            return ele.id === this.localModel.sender_id;
          });
          if (!sender) {
            this.localModel.sender_id = null;
          }
        }
        
        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    async fetchsenders() {
      // show loader
      this.showLoader();
      try {
        if (this.verifiedSendersRequired) {
          const { data } = await axios.get('/sender', {
            params: { verify: 1 },
          });
          if (data.length > 0) {
            this.senders = data;
          }
        } else {
          const { data } = await axios.get('/sender');
          if (data.length > 0) {
            this.senders = data;
          }
        }
        if (
          (this.$route.params.id === undefined && (this.localModel.sender_id === -1 || !this.localModel.sender_id)) ||
          (this.$route.params.id && (this.localModel.sender_id === -1 || !this.localModel.sender_id))
        ) {
          for (let i = 0; i < this.senders.length; i += 1) {
            if (this.senders[i].is_default === 1) {
              this.localModel.sender_id = this.senders[i].id;
            }
          }
        }
        if (this.localModel.sender_id) {
          const sender = this.senders.find((ele) => {
            return ele.id === this.localModel.sender_id;
          });
          if (!sender) {
            this.localModel.sender_id = null;
          }
        }

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.$emit('requestErrorAlert', error.response?.data?.error);
      }
    },

    // SMS and Email subject variables search function
    changedSearchVariable(searchQuery) {
      if (searchQuery) {
        this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject.filter(
          (ele) => ele.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
      } else {
        this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject;
      }
    },

    closeSubjectVariables() {
      this.showSubjectDropdown = false;
    },

    openSubjectVariables() {
      this.showSubjectDropdown = true;
    },

    closeSmsVariables() {
      this.showSmsVariableDropdown = false;
    },

    openSmsVariables() {
      this.showSmsVariableDropdown = true;
    },

    validate() {
      if (this.localModel.sendoption === 'email') {
        if (this.localModel.editor_type === 1) {
          this.validateEmailContent();
        }
        this.isEmailContentSubmitted = true;
        this.$v.$touch();
        let isInValid = true;
        if (this.localModel.editor_type === 1) {
          isInValid = this.$v.localModel.emailcontent.$invalid ||
            this.$v.localModel.emailsubject.$invalid ||
            !this.localModel.sender_id ||
            this.localModel.sender_id == -1;
        } else {
          isInValid = this.$v.localModel.emailcontent.$invalid ||
            this.$v.localModel.emailsubject.$invalid ||
            !this.localModel.emailContentSections ||
            !this.localModel.sender_id ||
            this.localModel.sender_id == -1;
        }
        this.$emit('on-validate', !isInValid);
        return !isInValid;
      }
      if (this.localModel.sendoption === 'sms') {
        this.isSmsContentSubmitted = true;
        const isValid =
          !this.$v.localModel.smscontent.$invalid &&
          this.localModel.sender_id &&
          this.localModel.sender_id != -1;
        this.$emit('on-validate', isValid);
        return isValid;
      }
      if (this.localModel.sendoption === 'postal') {
        this.isPostalContentSubmitted = true;
        const isValid = this.localModel.postal_sequence_id;
        this.$emit('on-validate', isValid);
        return isValid;
      }
    },

    validateEmailContent() {
      const editor = this.$refs ? this.$refs['campaign-email-editor'] : null;
      syncEditorContent(editor, (html) => {
        this.localModel.emailcontent = html;
      });
    },

    async validateForSaveAndExit() {
      if (this.localModel.sendoption === 'email') {
        if (this.localModel.editor_type === 1) {
          this.validateEmailContent();
        }
        this.isEmailContentSubmitted = true;
        this.$v.$touch();
        let isInValid = true;
        if (this.localModel.editor_type === 1) {
          isInValid = this.$v.localModel.emailcontent.$invalid ||
            this.$v.localModel.emailsubject.$invalid ||
            !this.localModel.sender_id ||
            this.localModel.sender_id == -1;
        } else {
          isInValid = this.$v.localModel.emailcontent.$invalid ||
            this.$v.localModel.emailsubject.$invalid ||
            !this.localModel.emailContentSections ||
            !this.localModel.sender_id ||
            this.localModel.sender_id == -1;
        }
        return !isInValid;
      }
      if (this.localModel.sendoption === 'sms') {
        this.isSmsContentSubmitted = true;
        return (
          !this.$v.localModel.smscontent.$invalid &&
          this.localModel.sender_id &&
          this.localModel.sender_id != -1
        );
      }
      if (this.localModel.sendoption === 'postal') {
        this.isPostalContentSubmitted = true;
        return this.localModel.postal_sequence_id;
      }
    },

    // Set the variable in the content for the SMS
    copySmsvariable(option) {
      // target element to insert variable
      const content = document.getElementById('sms-text-area');

      let position;

      // check if content is available and selectionStart is available
      if (content && content.selectionStart != null && content.selectionStart !== undefined) {
        position = content.selectionStart;
      } else {
        return false;
      }
      this.localModel.smscontent = [
        this.localModel.smscontent.slice(0, position),
        option.value,
        this.localModel.smscontent.slice(position),
      ].join('');

      content.focus();

      content.setSelectionRange(
        position + option.value.length,
        position + option.value.length
      );
      this.showSmsVariableDropdown = false;
      this.smsVariableSearch = '';
      this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject;
    },

    // Set the variable in the subject for the Email
    copyVariableInSubject(option) {
      if (!this.localModel.emailsubject) {
        this.localModel.emailsubject = '';
      }
      this.localModel.emailsubject += `${option.value}`;
      this.showSubjectDropdown = false;
      this.emailSubjectVariableSearch = '';
      this.dynamic_variables_sms_content_subject = this.dynamic_variables_sms_subject;
    },

    checkStep4validity() {
      this.$emit(
        'stepValidity',
        !!(
          (
            this.localModel.sendoption === 'email' &&
            this.localModel.sender_id &&
            this.localModel.sender_id !== -1 &&
            this.localModel.emailsubject &&
            this.localModel.emailcontent
          ) ||
          (
            this.localModel.sendoption === 'sms' &&
            this.localModel.sender_id &&
            this.localModel.sender_id !== -1 &&
            this.localModel.smscontent
          ) ||
          (
            this.localModel.sendoption === 'postal' &&
            this.localModel.postal_sequence_id
          )
        ),
        'step4'
      );
    },

    async proceeedSendingTestEmail() {
      if (
        this.localModel.sendoption === 'email' &&
        this.localModel.sender_id &&
        this.localModel.sender_id !== -1 &&
        !this.$v.localModel.emailcontent.$invalid &&
        !this.$v.localModel.emailsubject.$invalid
      ) {
        await this.$refs.sendTestMail.sendTestEmail(
          this.localModel.sender_id,
          this.localModel.emailsubject,
          this.localModel.emailpreviewtext,
          this.localModel.emailcontent
        );
      } else if (!this.localModel.sender_id || this.localModel.sender_id === -1) {
        // show error message
        this.$emit('requestErrorAlert', this.$t('frontend_groovemail_send_test_email_sender_id_error'));
      } else if (this.$v.localModel.emailsubject.$invalid) {
        // show error message
        this.$emit('requestErrorAlert', this.$t('frontend_groovemail_send_test_email_subject_error'));
      } else if (this.$v.localModel.emailcontent.$invalid) {
        // show error message
        this.$emit('requestErrorAlert', this.$t('frontend_groovemail_send_test_email_content_error'));
      } else {
        // show error message
        this.$emit('requestErrorAlert', this.$t('frontend_error'));
      }
    },

    async fetchPostalCampaigns() {
      // const closemIntegrationId = this.fetchClosemIntegration();

      // show loader
      this.showLoader();

      try {
        const postalCampaigns = await axios.get('/postal/broadcast');
        
        if (postalCampaigns.data.status.toLowerCase() === 'fail') {
          // show error message
          this.$emit('requestErrorAlert', this.$t('frontend_groovemail_campaigns_create_postal_integration_invalid'));
          this.postalCampaigns = [];
          this.fetchClosemCredits();
        } else if (
          postalCampaigns &&
          postalCampaigns.data
        ) {
          if (
            postalCampaigns.data.response &&
            postalCampaigns.data.response.campaigns &&
            postalCampaigns.data.response.campaigns.length
          ) {
            this.postalCampaigns = postalCampaigns.data.response.campaigns;
          }
          if (
            postalCampaigns.data.postal
          ) {
            this.fetchClosemCredits(postalCampaigns.data.postal);
          }
        }
      } catch (error) {
        // show error message
        this.$emit('requestErrorAlert', error.response?.data?.error);
        this.postalCampaigns = [];
        this.fetchClosemCredits();
      }
    },

    fetchClosemCredits(postalData) {
      this.credits_large_us =
        (postalData && postalData.credits_large_us) ? postalData.credits_large_us : 0;
      this.credits_small_us =
        (postalData && postalData.credits_small_us) ? postalData.credits_small_us : 0;
      this.credits_large_int =
        (postalData && postalData.credits_large_int) ? postalData.credits_large_int : 0;
      this.credits_small_int =
        (postalData && postalData.credits_small_int) ? postalData.credits_small_int : 0;
    },

    fetchClosemIntegration() {
      if (this.integrations && this.integrations.length) {
        return this.integrations[this.integrations.length - 1].id;
      }
      return 0;
    },

    redirectToCLosemDashboard() {
      window.open('https://app.closem.ai/index.php?m=campaigns');
    },

    redirectToCLosemSignupPage() {
      const user = auth.authenticatedUser();
      window.open(`${'https://groove.closem.ai/?fpr=groove'}&name=${user.name}&email=${user.email}&phone=`);
    },

    redirectToClosemCreditsPage() {
      window.open('https://closem.ai/postalcredits-free-tier/');
    },
  },

  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },

    'localModel.editor_type': async function handleEmailTypeChange(newEmailType, oldEmailType) {
      if (this.editorTypeChangedInternally) {
        this.editorTypeChangedInternally = false;
      } else if (
        ((newEmailType === 1 && oldEmailType === 3) || newEmailType === 3) &&
        this.localModel.emailcontent
      ) {
        this.showEmailComposers = false;
        
        // open confirmation editor type
        this.openEditorTypeConfirmation(newEmailType, oldEmailType);
      }
    },

    'localModel.sender_id': function() {
      this.changesMadeInStep = true;
      this.checkStep4validity();
    },

    'localModel.postal_sequence_id': function() {
      this.changesMadeInStep = true;
      this.checkStep4validity();
    },

    'localModel.emailsubject': function() {
      this.changesMadeInStep = true;
      this.checkStep4validity();
    },

    'localModel.emailcontent': async function () {
      this.changesMadeInStep = true;
      this.checkStep4validity();
    },

    'localModel.smscontent': function() {
      this.changesMadeInStep = true;
      this.checkStep4validity();
    },

    'localModel.emailpreviewtext': function() {
      this.changesMadeInStep = true;
    },
  },
};
</script>

<style>
.gm-campaign-create .gs-wizard-card > p.gm-step4__postal-intro {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: baseline;
}

.gm-campaign-create .gm-step4__postal-signup {
  display: flex;
}

.gm-campaign-create .gm-step4__credits {
  padding-top: 18px;
  border-top: 1px solid var(--gs-border);
}

.gm-campaign-create .gm-step4__credits-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.gm-campaign-create .gm-step4__credits-head h3 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.3;
}

.gm-campaign-create .gm-step4__credits-copy {
  margin: 0 0 10px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

.gm-campaign-create .gm-step4__credits-copy strong {
  color: var(--gs-foreground);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.gm-campaign-create .gm-step4__credits .gm-stat-grid {
  margin-top: 14px;
}

.gm-campaign-create .gm-step4__sender {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  max-width: 640px;
}

.gm-campaign-create .gm-step4__sender-field {
  min-width: 0;
}

.gm-campaign-create .gm-step4__fields {
  max-width: 560px;
}

.gm-campaign-create .gm-step4__subject-input > input {
  padding-right: 44px;
}

/* Toolbar above the SMS composer */
.gm-campaign-create .gm-step4__toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 10px;
}

.gm-campaign-create .gm-step4__toolbar-icon,
.gm-campaign-create .gm-step4__preview-icon {
  width: 18px;
  height: 18px;
}

/* Insert-variable popover (SMS body and email subject) */
.gm-campaign-create .gm-variables {
  position: relative;
}

.gm-campaign-create .gm-variables__anchor {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
}

.gm-campaign-create .gm-variables__inline-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  color: var(--gs-primary);
  background: var(--gs-primary-soft);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.gm-campaign-create .gm-variables__inline-trigger:hover {
  color: var(--gs-primary-foreground);
  background: var(--gs-primary);
}

.gm-campaign-create .gm-variables__inline-trigger:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gm-campaign-create .gm-variables__menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 10;
  width: 290px;
  max-width: calc(100vw - 48px);
  padding: 12px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-md);
}

.gm-campaign-create .gm-variables__list {
  max-height: 260px;
  margin-top: 10px;
}

.gm-campaign-create .gm-variables__item {
  display: block;
  width: 100%;
  padding: 8px 10px;
  color: var(--gs-foreground);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
}

.gm-campaign-create .gm-variables__item:hover {
  background: var(--gs-muted);
}

/* Builder actions */
.gm-campaign-create .gm-step4__template-name {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
  padding: 10px 12px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gm-campaign-create .gm-step4__template-name strong {
  color: var(--gs-foreground);
  font-weight: 650;
}

.gm-campaign-create .gm-step4__builder-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.gm-campaign-create .gm-step4__editor {
  min-width: 0;
}

.gm-campaign-create .gm-professional-email-editor-actions {
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--gs-border);
}

/* SendTestEmail keeps its own label; the card title replaces it. */
.gm-campaign-create .gm-step4__test > .max-w-full > div:first-child {
  display: none;
}

.gm-campaign-create .gm-step4__test .border.rounded-lg {
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

/* Email preview modal */
.gm-campaign-create .gm-step4__email-preview-frame {
  min-height: 200px;
  padding: 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

/* Keep long template and editor content inside the modal viewport. */
.gm-professional-email-templates-modal__body,
.gm-professional-email-editor-modal__body {
  box-sizing: border-box;
  height: calc(100vh - 170px);
  max-height: calc(100vh - 170px);
  min-height: 0 !important;
  overflow-x: hidden;
  overflow-y: auto;
}

/* These two large workspaces behave like a side shelf instead of a cramped
   centered popup, leaving the campaign page visible behind them. */
.gd-modal.gm-professional-email-templates-modal > .gd-modal__content,
.gd-modal.gm-professional-email-editor-modal > .gd-modal__content {
  align-items: stretch !important;
  justify-content: flex-end !important;
  padding: 0 !important;
}

.gd-modal.gm-professional-email-templates-modal > .gd-modal__content > div,
.gd-modal.gm-professional-email-editor-modal > .gd-modal__content > div {
  width: min(80vw, 1200px) !important;
  max-width: 80vw !important;
  height: 100vh !important;
  max-height: 100vh !important;
  margin: 0 !important;
  border-radius: 16px 0 0 16px !important;
}

@media (max-width: 900px) {
  .gd-modal.gm-professional-email-templates-modal > .gd-modal__content > div,
  .gd-modal.gm-professional-email-editor-modal > .gd-modal__content > div {
    width: 100vw !important;
    max-width: 100vw !important;
    border-radius: 0 !important;
  }
}

@media (max-width: 640px) {
  .gm-campaign-create .gm-step4__sender {
    grid-template-columns: 1fr;
  }

  .gm-campaign-create .gm-step4__credits-head {
    flex-wrap: wrap;
  }
}

/* Legacy global helpers used by other pages (forms, sequences); left as-is. */
.content-html-area textarea {
  background: black;
  color: white;
  padding-top: 4px;
  padding-bottom: 4px;
}

.text-uppercase {
  text-transform: uppercase;
}

.disabled-preview {
  background-color: #f8f8fc !important;
  box-shadow: none !important;
}

#email-templates-modal .bg-white.rounded-lg.shadow-2xl.relative.flex.flex-col.overflow-hidden {
  width: 1000px !important;
  max-width: 90%;
  max-height: 92vh;
}
</style>
